import { CommonModule } from '@angular/common';
import { Component, DoCheck, ElementRef, HostBinding, ViewChild, inject } from '@angular/core';
import { AxisDriversComponent as ParentAxisDriversComponent } from '@shared-lib/global-components/axis-drivers/axis-drivers.component';
import { MixedStackbarChartComponent } from '../../chart-components/mixed-stackbar-chart/mixed-stackbar-chart.component';
import { TreemapChartComponent } from '../../chart-components/treemap-chart/treemap-chart.component';
import { CagrHoverChartComponent } from './cagr-hover-chart.component';
import { MarketShareProgressComponent } from './market-share-progress.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import { Subscription, timeout } from 'rxjs';

type DynamicProgressSegmentSizes = { weak: number; fair: number; good: number; weakEnd: number; fairEnd: number; min: number; max: number };

@Component({
  selector: 'app-axis-drivers',
  standalone: true,
  imports: [CommonModule, MixedStackbarChartComponent, TreemapChartComponent, CagrHoverChartComponent, MarketShareProgressComponent],
  templateUrl: './axis-drivers.component.html',
  styleUrls: ['./axis-drivers.component.scss'],
})
export class AxisDriversComponent extends ParentAxisDriversComponent implements DoCheck {
  private static readonly UNAVAILABLE_NOTE = 'Note: Due to data unavailability, we are providing the default score of 5';
  private readonly hoverConfigService = inject(ConfigService);
  private readonly hoverGlobalFilterService = inject(GlobalFilterService);
  private readonly customResponsiveLayoutService = inject(ResponsiveLayoutService);
  @ViewChild('hoverCard') private hoverCard?: ElementRef<HTMLElement>;
  activeHoverRow: any = null;
  activeHoverConfig: any = null;
  activeHoverStyle: Record<string, string> | null = null;
  hoverChartLoading = false;
  hoverChartError = '';
  hoverUnavailableNote = '';
  hoverTooltipValue = '';
  hoverTooltipLoading = false;
  hoverTooltipError = '';
  salesProgressBoundaryLabels: Array<{ text: string; left: number; edgeClass: string }> = [];
  private hideHoverTimer?: ReturnType<typeof setTimeout>;
  private hoverLoadSub?: Subscription;
  private lastRowOrderSignature = '';
  private activeHoverAnchorRect: { left: number; right: number; top: number; width: number; height: number } | null = null;
  private activeHoverCursorPoint: { x: number; y: number } | null = null;
  private activeHoverRequestParams: Record<string, any> = {};
  // getRowProgressScaleLabels/getDynamicProgressSegmentSizes are called from several template
  // bindings and re-run on every mousemove-triggered change detection pass while hovering (can be
  // 60+ times/sec). Without caching, each call rebuilds arrays/objects from scratch, which is what
  // pegs the main thread and makes the tab feel frozen — this WeakMap recomputes only when the row
  // or its hover response actually changes.
  private readonly progressComputeCache = new WeakMap<any, { responseData: any; scaleLabels?: Array<{ text: string; left: number; edgeClass: string }>; segmentSizes?: DynamicProgressSegmentSizes | null }>();

  get resolvedHeight(): string {
    return this.customResponsiveLayoutService.resolveWidgetHeight(this.data);
  }

  @HostBinding('style.display')
  get hostDisplay(): string {
    return 'block';
  }

  @HostBinding('style.width')
  get hostWidth(): string {
    return '100%';
  }

  @HostBinding('style.min-height')
  get hostMinHeight(): string {
    return '0';
  }

  @HostBinding('style.height')
  get hostHeight(): string {
    return this.resolvedHeight;
  }

  ngDoCheck(): void {
    this.applyConfiguredRowOrder();
  }

  showHoverChart(row: any, event?: MouseEvent): void {
    this.clearHideHoverTimer();
    const chartConfig = this.getHoverChartConfig(row);
    if (!chartConfig) {
      this.hideHoverChart();
      return;
    }

    this.activeHoverRow = row;
    const type = this.normalizeHoverType(chartConfig?.type);
    this.activeHoverConfig = {
      ...chartConfig,
      type,
      ...(type === 'treemap-chart' && chartConfig?.apiUrl ? { skipAutoLoad: true } : {}),
      widgetId: chartConfig.widgetId || `${this.data?.widgetId || 'axis-driver'}-${this.normalizeKey(row?.label)}-hover-chart`,
    };
    this.hoverChartLoading = type !== 'tooltip' && Boolean(chartConfig?.apiUrl);
    this.hoverChartError = '';
    this.hoverUnavailableNote = '';
    this.hoverTooltipValue = '';
    this.hoverTooltipError = '';
    this.hoverTooltipLoading = type === 'tooltip';
    this.salesProgressBoundaryLabels = [];
    this.activeHoverCursorPoint = event ? { x: event.clientX, y: event.clientY } : null;
    this.activeHoverAnchorRect = event ? this.getHoverAnchorRect(event, chartConfig) : null;
    this.activeHoverStyle = event
      ? this.buildHoverStyle(event, chartConfig)
      : this.activeHoverStyle || this.buildHoverStyle(event, chartConfig);
    this.scheduleAutoSizedHoverPosition();

    this.loadHoverChartData(chartConfig, row);
    console.log("Ui changes");
  }

  hideHoverChart(): void {
    this.clearHideHoverTimer();
    this.hoverLoadSub?.unsubscribe();
    this.hoverLoadSub = undefined;
    this.activeHoverRow = null;
    this.activeHoverConfig = null;
    this.activeHoverStyle = null;
    this.activeHoverAnchorRect = null;
    this.activeHoverCursorPoint = null;
    this.activeHoverRequestParams = {};
    this.hoverChartLoading = false;
    this.hoverChartError = '';
    this.hoverUnavailableNote = '';
    this.hoverTooltipValue = '';
    this.hoverTooltipLoading = false;
    this.hoverTooltipError = '';
    this.salesProgressBoundaryLabels = [];
  }

  keepHoverChartOpen(): void {
    this.clearHideHoverTimer();
  }

  scheduleHideHoverChart(): void {
    this.clearHideHoverTimer();
    this.hideHoverTimer = setTimeout(() => this.hideHoverChart(), 120);
  }

  override ngOnDestroy(): void {
    this.hideHoverChart();
    super.ngOnDestroy();
  }

  getHoverChartConfig(row: any): any {
    // Screen-wide master switch: `"hoverPopupsEnabled": false` on the widget
    // turns off every hover popup it owns in one go, without touching any of
    // the individual hoverCharts entries below. Flip it back to true (or
    // remove it, default is enabled) to restore them all at once.
    if ((this.data as any)?.hoverPopupsEnabled === false) {
      return null;
    }
    const labelKey = String(row?.label || '').trim().toLowerCase();
    const originalLabelKey = String(row?.originalLabel || '').trim().toLowerCase();
    const hoverCharts = (this.data as any)?.hoverCharts || {};
    const config = hoverCharts[labelKey] || hoverCharts[row?.label] || hoverCharts[originalLabelKey] || hoverCharts[row?.originalLabel] || null;
    // Key-based on/off switch: set `"enabled": false` on a hoverCharts entry in
    // the dashboard JSON to turn its popup off without deleting the config —
    // flip it back to true (or remove the key, default is enabled) whenever
    // it's needed again.
    if (config?.enabled === false) {
      return null;
    }
    return config;
  }

  handleRowMouseEnter(row: any, event: MouseEvent): void {
    if (this.isLabelOnlyHover(row)) {
      return;
    }

    this.showHoverChart(row, event);
  }

  handleRowMouseMove(row: any, event: MouseEvent): void {
    if (this.isLabelOnlyHover(row)) {
      return;
    }

    this.updateHoverChartPosition(row, event);
  }

  handleRowMouseLeave(row: any): void {
    if (this.isLabelOnlyHover(row)) {
      return;
    }

    this.scheduleHideHoverChart();
  }

  handleLabelMouseEnter(row: any, event: MouseEvent): void {
    if (!this.isLabelOnlyHover(row)) {
      return;
    }

    this.showHoverChart(row, event);
  }

  handleLabelMouseMove(row: any, event: MouseEvent): void {
    if (!this.isLabelOnlyHover(row)) {
      return;
    }

    this.updateHoverChartPosition(row, event);
  }

  handleLabelMouseLeave(row: any): void {
    if (!this.isLabelOnlyHover(row)) {
      return;
    }

    this.scheduleHideHoverChart();
  }

  getUpdateLabel(row: any): string {
    const update = this.toDisplayText(row?.update);
    if (!update) {
      return '';
    }

    if (update === update.toUpperCase()) {
      const lowerUpdate = update.toLowerCase();
      return lowerUpdate.charAt(0).toUpperCase() + lowerUpdate.slice(1);
    }

    return update;
  }

  handleHoverCardMouseEnter(): void {
    if (this.activeHoverConfig?.popupInteractive === false) {
      return;
    }

    this.keepHoverChartOpen();
  }

  shouldShowRowProgress(row: any): boolean {
    const hoverType = this.normalizeHoverType(this.activeHoverConfig?.type);
    if (hoverType === 'market-share-progress' && this.activeHoverConfig?.showSupplementalProgressBar !== true) {
      return false;
    }

    const config = this.getRowProgressConfig();
    if (!config?.enabled) {
      return false;
    }

    const labels = this.resolveProgressLabels(config);
    if (!labels.size) {
      return true;
    }

    return labels.has(this.normalizeRowLabel(row?.label)) || labels.has(this.normalizeRowLabel(row?.originalLabel));
  }

  getRowProgressMarkerStyle(row: any): Record<string, string> {
    const config = this.getResolvedRowProgressConfig(row);
    return {
      left: `${this.resolveRowProgressPercent(row)}%`,
      ...(this.isProgressLegendShown(row) ? { background: String(config?.markerLineColor || '#0b3a82') } : {}),
    };
  }

  // showProgressLegend: the gauge points are plain coloured lines, named in a legend above the gauge
  // (e.g. "CP = 10.5K", "TP = 13K") instead of labels on the gauge itself.
  // The legend list is rebuilt on every change-detection pass; tracking by position keeps its DOM in place.
  trackByLegendIndex(index: number): number {
    return index;
  }

  isProgressLegendShown(row: any): boolean {
    return this.getResolvedRowProgressConfig(row)?.showProgressLegend === true;
  }

  getRowProgressLegend(row: any): Array<{ text: string; color: string }> {
    if (!this.isProgressLegendShown(row)) {
      return [];
    }
    const config = this.getResolvedRowProgressConfig(row);
    const items: Array<{ text: string; color: string }> = [];
    const markerText = this.getRowProgressMarkerValueLabel(row);
    if (markerText) {
      items.push({
        text: config?.markerLegendLabel ? `${config.markerLegendLabel} = ${markerText}` : markerText,
        color: String(config?.markerLineColor || '#0b3a82'),
      });
    }
    const targetText = this.getRowProgressTargetMarkerText(row);
    if (targetText) {
      items.push({
        text: config?.targetMarkerLabel ? `${config.targetMarkerLabel} = ${targetText}` : targetText,
        color: String(config?.targetMarkerLineColor || '#0f766e'),
      });
    }
    return items;
  }

  getRowProgressMarkerClasses(row: any): Record<string, boolean> {
    const config = this.getResolvedRowProgressConfig(row);
    const placement = String(config?.markerPlacement || config?.markerValuePlacement || '').trim().toLowerCase();
    return {
      'axis-drivers__progress-marker--above': placement === 'above',
      'axis-drivers__progress-marker--line': this.isProgressLegendShown(row),
    };
  }

  getRowProgressReferenceStyle(row: any): Record<string, string> {
    return {
      left: `${this.resolveRowProgressReferencePercent(row)}%`,
    };
  }

  // Weak/fair (red/yellow) boundary label — mirrors getRowProgressReferenceLabel/Style but anchors
  // to the weakEnd boundary instead of fairEnd. Needed because once weakEndValue is itself dynamic
  // (e.g. "fairEnd - 10" via weakEndValueOffset), a scaleLabels entry with a hardcoded `position`
  // would drift out of alignment with the actual (now-moving) color-band boundary.
  getRowProgressSecondaryReferenceLabel(row: any): string {
    const config = this.getResolvedRowProgressConfig(row);
    const rawValue = this.resolveConfiguredProgressValue(config?.secondaryReferenceValuePath, config?.secondaryReferenceValueKey);
    if (rawValue === null) {
      return '';
    }

    // formatProgressValueLabel has no "offset" concept — apply divisor/multiplier then the offset
    // ourselves (same order as resolveProgressBoundaryValue) so this label matches the actual
    // weakEnd boundary value (e.g. "segment_margin*100 - 10"), not just the raw reference value.
    const divisor = Number(config?.secondaryReferenceValueDivisor ?? config?.valueDivisor);
    const multiplier = Number(config?.secondaryReferenceValueMultiplier ?? config?.valueMultiplier);
    let scaledValue = rawValue;
    if (Number.isFinite(divisor) && divisor !== 0) {
      scaledValue = rawValue / divisor;
    } else if (Number.isFinite(multiplier)) {
      scaledValue = rawValue * multiplier;
    }

    const offset = Number(config?.secondaryReferenceValueOffset);
    if (Number.isFinite(offset)) {
      scaledValue += offset;
    }

    return this.formatProgressValueLabel(scaledValue, {
      suffix: config?.secondaryReferenceValueSuffix ?? config?.valueSuffix ?? '%',
      decimals: config?.secondaryReferenceValueDecimals ?? config?.valueDecimals,
    });
  }

  getRowProgressSecondaryReferenceStyle(row: any): Record<string, string> {
    return {
      left: `${this.resolveRowProgressSecondaryReferencePercent(row)}%`,
    };
  }

  getRowProgressMarkerValueStyle(row: any): Record<string, string> {
    const config = this.getResolvedRowProgressConfig(row);
    const placement = String(config?.markerValuePlacement || '').trim().toLowerCase();
    const offsetY = Number(config?.markerValueOffsetY);
    return {
      left: `${this.resolveRowProgressPercent(row)}%`,
      ...(Number.isFinite(offsetY)
        ? { top: placement === 'above' ? `${offsetY}px` : `calc(100% + ${offsetY}px)` }
        : {}),
    };
  }

  getRowProgressMarkerValueClasses(row: any): Record<string, boolean> {
    const config = this.getResolvedRowProgressConfig(row);
    const placement = String(config?.markerValuePlacement || '').trim().toLowerCase();
    return {
      'axis-drivers__progress-marker-value--above': placement === 'above',
    };
  }

  private getProgressCacheEntry(row: any): { responseData: any; scaleLabels?: Array<{ text: string; left: number; edgeClass: string }>; segmentSizes?: DynamicProgressSegmentSizes | null } {
    const responseData = this.activeHoverConfig?.responseData;
    const existing = this.progressComputeCache.get(row);
    if (existing && existing.responseData === responseData) {
      return existing;
    }

    const entry = { responseData };
    this.progressComputeCache.set(row, entry);
    return entry;
  }

  private getCachedDynamicProgressSegmentSizes(row: any, config: any): DynamicProgressSegmentSizes | null {
    const cacheEntry = this.getProgressCacheEntry(row);
    if (cacheEntry.segmentSizes !== undefined) {
      return cacheEntry.segmentSizes;
    }

    const sizes = this.getDynamicProgressSegmentSizes(config);
    cacheEntry.segmentSizes = sizes;
    return sizes;
  }

  getRowProgressTrackStyle(row: any): Record<string, string> {
    const config = this.getResolvedRowProgressConfig(row);
    const dynamicSizes = this.getCachedDynamicProgressSegmentSizes(row, config);
    // Dynamic weak/fair boundaries (e.g. weakEnd = fairEnd - 10) can sit anywhere on the min..max
    // scale depending on the response value — drawing the bands at their true numeric widths could
    // make one band swallow most of the bar (e.g. a huge red segment) whenever the boundary lands
    // far from center. Equal thirds keeps the bands visually consistent for any response value;
    // resolveRowProgressPercent/ReferencePercent map values onto these same thirds so the marker
    // and boundary ticks stay aligned with what's actually drawn.
    const weak = dynamicSizes ? 1 : this.resolveProgressSegmentSize(config?.weakSegmentSize, 42);
    const fair = dynamicSizes ? 1 : this.resolveProgressSegmentSize(config?.fairSegmentSize, 16);
    const good = dynamicSizes ? 1 : this.resolveProgressSegmentSize(config?.goodSegmentSize, 42);
    return {
      'grid-template-columns': `${weak}fr ${fair}fr ${good}fr`,
    };
  }

  // Lets a row's red/yellow/green split follow a value that only arrives with the
  // response (e.g. margin's fair_margin), instead of the static *SegmentSize config.
  // Only engages when weakEndValue + a fairEndValue(Path|Key) are configured, so rows
  // without them (sales, cagr) keep using the existing static segment sizes untouched.
  // Resolves a color-band boundary (weakEnd/fairEnd) that can be a plain static number
  // (weakEndValue/fairEndValue) OR derived from a response path (weakEndValuePath/fairEndValuePath),
  // with optional multiplier/divisor (e.g. a 0-1 fraction field needs *100 to read as a percent)
  // and an optional offset applied last (e.g. "fairEnd - 10" to keep a constant-width yellow band).
  private resolveProgressBoundaryValue(config: any, prefix: 'weakEnd' | 'fairEnd'): number | null {
    const staticValue = Number(config?.[`${prefix}Value`]);
    let value = Number.isFinite(staticValue)
      ? staticValue
      : this.resolveConfiguredProgressValue(config?.[`${prefix}ValuePath`], config?.[`${prefix}ValueKey`]);

    if (value === null || !Number.isFinite(value)) {
      return null;
    }

    const divisor = Number(config?.[`${prefix}ValueDivisor`]);
    const multiplier = Number(config?.[`${prefix}ValueMultiplier`]);
    if (Number.isFinite(divisor) && divisor !== 0) {
      value = value / divisor;
    } else if (Number.isFinite(multiplier)) {
      value = value * multiplier;
    }

    const offset = Number(config?.[`${prefix}ValueOffset`]);
    if (Number.isFinite(offset)) {
      value = value + offset;
    }

    return value;
  }

  private getDynamicProgressSegmentSizes(config: any): DynamicProgressSegmentSizes | null {
    try {
      const weakEnd = this.resolveProgressBoundaryValue(config, 'weakEnd');
      if (weakEnd === null) {
        return null;
      }

      const fairEnd = this.resolveProgressBoundaryValue(config, 'fairEnd');
      if (fairEnd === null) {
        return null;
      }

      // minValuePath / maxValuePath let the scale ends follow the response too (e.g. a price scale that
      // depends on this row's target price); otherwise the static min / max apply.
      const min = Number(this.resolveConfiguredProgressValue(config?.minValuePath, config?.minValueKey) ?? config?.min ?? -50);
      const max = Number(this.resolveConfiguredProgressValue(config?.maxValuePath, config?.maxValueKey) ?? config?.max ?? 50);
      if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
        return null;
      }

      const weak = Math.max(0, weakEnd - min);
      const fair = Math.max(0, fairEnd - weakEnd);
      const good = Math.max(0, max - fairEnd);
      if (weak + fair + good <= 0) {
        return null;
      }

      return { weak, fair, good, weakEnd, fairEnd, min, max };
    } catch (err) {
      console.error('[AXIS_DRIVERS_DEBUG_c91e4d] getDynamicProgressSegmentSizes threw — falling back to static segment sizes', { config, err });
      return null;
    }
  }

  // Maps a raw value onto the equal-thirds layout drawn by getRowProgressTrackStyle: values at or
  // below weakEnd fall in the first third (scaled by how far into [min, weakEnd] they are), values
  // between weakEnd and fairEnd fall in the middle third, and values at or above fairEnd fall in
  // the last third. This keeps the marker inside the correctly-colored band even though the bands
  // themselves no longer reflect true numeric proportions.
  private mapValueToEqualThirdsPercent(value: number, sizes: { weakEnd: number; fairEnd: number; min: number; max: number }): number {
    const { weakEnd, fairEnd, min, max } = sizes;
    if (value <= weakEnd) {
      const span = weakEnd - min;
      const ratio = span > 0 ? (value - min) / span : 0;
      return Math.max(0, Math.min(1, ratio)) * (100 / 3);
    }

    if (value <= fairEnd) {
      const span = fairEnd - weakEnd;
      const ratio = span > 0 ? (value - weakEnd) / span : 0;
      return (100 / 3) + Math.max(0, Math.min(1, ratio)) * (100 / 3);
    }

    const span = max - fairEnd;
    const ratio = span > 0 ? (value - fairEnd) / span : 0;
    return (200 / 3) + Math.max(0, Math.min(1, ratio)) * (100 / 3);
  }

  getRowProgressStartLabel(row: any): string {
    const config = this.getResolvedRowProgressConfig(row);
    return String(config?.startLabel ?? config?.minLabel ?? '').trim();
  }

  getRowProgressEndLabel(row: any): string {
    const config = this.getResolvedRowProgressConfig(row);
    return String(config?.endLabel ?? config?.maxLabel ?? '').trim();
  }

  getRowProgressScaleLabels(row: any): Array<{ text: string; left: number; edgeClass: string }> {
    const cacheEntry = this.getProgressCacheEntry(row);
    if (cacheEntry.scaleLabels) {
      return cacheEntry.scaleLabels;
    }

    const config = this.getResolvedRowProgressConfig(row);
    const configuredLabels = Array.isArray(config?.scaleLabels) ? config.scaleLabels : [];
    const labels = configuredLabels
      .map((label: any) => {
        try {
          const literalText = typeof label?.text === 'string' ? label.text.trim() : '';
          const resolvedValue = literalText ? null : this.resolveConfiguredProgressValue(label?.valuePath, label?.valueKey);
          const valueOffset = Number(label?.valueOffset);
          const value = resolvedValue !== null && Number.isFinite(valueOffset) ? resolvedValue + valueOffset : resolvedValue;
          const text = literalText || this.formatProgressValueLabel(value, {
            suffix: label?.suffix ?? config?.valueSuffix,
            divisor: label?.divisor ?? config?.valueDivisor,
            multiplier: label?.multiplier ?? config?.valueMultiplier,
            decimals: label?.decimals ?? config?.valueDecimals,
            prefix: label?.prefix ?? config?.valuePrefix,
            useGrouping: label?.useGrouping ?? config?.valueUseGrouping,
            compact: label?.compact ?? config?.valueCompact,
          });
          const left = Number(label?.position);
          if (!text || !Number.isFinite(left)) {
            return null;
          }

          const safeLeft = Math.max(0, Math.min(100, left));
          const edgeClass = safeLeft <= 0 ? 'start' : safeLeft >= 100 ? 'end' : 'middle';
          return { text, left: safeLeft, edgeClass };
        } catch (err) {
          console.error('[AXIS_DRIVERS_DEBUG_c91e4d] scaleLabels entry threw — skipping this label', { label, err });
          return null;
        }
      })
      .filter((label: { text: string; left: number; edgeClass: string } | null): label is { text: string; left: number; edgeClass: string } => Boolean(label));

    const result = labels.length
      ? labels
      : this.normalizeRowLabel(row?.originalLabel || row?.label) === 'sales'
        ? this.getFallbackSalesProgressScaleLabels()
        : labels;

    cacheEntry.scaleLabels = result;
    return result;
  }

  getRowProgressScaleLabelStyle(label: { left: number }): Record<string, string> {
    return {
      left: `${label.left}%`,
    };
  }

  hasRowProgressScaleLabels(row: any): boolean {
    return this.getRowProgressScaleLabels(row).length > 0;
  }

  isSalesProgressRow(row: any): boolean {
    const normalizedOriginalLabel = this.normalizeRowLabel(row?.originalLabel);
    const normalizedLabel = this.normalizeRowLabel(row?.label);
    return normalizedOriginalLabel === 'sales' || normalizedLabel === 'sales' || normalizedLabel.startsWith('sales ');
  }

  getSalesProgressGradeValue(path: string): string {
    const value = this.resolveConfiguredProgressValue(path, '');
    return this.formatProgressValueLabel(value, {
      divisor: 1000000,
      decimals: 1,
      suffix: 'M',
    });
  }

  getSalesProgressBoundaryLabels(row: any): Array<{ text: string; left: number; edgeClass: string }> {
    if (!this.isSalesProgressRow(row)) {
      return [];
    }

    return this.salesProgressBoundaryLabels;
  }

  getSalesProgressBoundaryLabelStyle(label: { left: number; edgeClass?: string }): Record<string, string> {
    if (label.edgeClass === 'start') {
      return {
        left: '0%',
        transform: 'translateX(0)',
      };
    }

    if (label.edgeClass === 'end') {
      return {
        left: '100%',
        transform: 'translateX(-25%)',
      };
    }

    return {
      left: `${label.left}%`,
    };
  }

  getRowProgressReferenceLabel(row: any): string {
    const config = this.getResolvedRowProgressConfig(row);
    const value = this.resolveConfiguredProgressValue(config?.referenceValuePath, config?.referenceValueKey);
    return this.formatProgressValueLabel(value, {
      suffix: config?.referenceValueSuffix ?? config?.valueSuffix ?? '%',
      divisor: config?.referenceValueDivisor ?? config?.valueDivisor,
      multiplier: config?.referenceValueMultiplier ?? config?.valueMultiplier,
      decimals: config?.referenceValueDecimals ?? config?.valueDecimals,
    });
  }

  getRowProgressMarkerValueLabel(row: any): string {
    const config = this.getResolvedRowProgressConfig(row);
    const value = this.resolveConfiguredProgressValue(config?.markerValuePath, config?.markerValueKey);
    const text = this.formatProgressValueLabel(value, {
      suffix: config?.markerValueSuffix ?? config?.valueSuffix ?? '%',
      divisor: config?.markerValueDivisor ?? config?.valueDivisor,
      multiplier: config?.markerValueMultiplier ?? config?.valueMultiplier,
      decimals: config?.markerValueDecimals ?? config?.valueDecimals,
      prefix: config?.markerValuePrefix ?? config?.valuePrefix,
      useGrouping: config?.markerValueUseGrouping ?? config?.valueUseGrouping,
      compact: config?.markerValueCompact ?? config?.valueCompact,
    });
    // markerValueLabelPrefix names the point, e.g. "CP " for the current price.
    return text && config?.markerValueLabelPrefix ? `${config.markerValueLabelPrefix}${text}` : text;
  }

  // Optional second point on the gauge (e.g. TP, the target price), read from targetMarkerValuePath and
  // placed on the same scale as the main marker.
  private getRowProgressTargetMarkerValue(row: any): number | null {
    const config = this.getResolvedRowProgressConfig(row);
    return config?.targetMarkerValuePath ? this.resolveConfiguredProgressValue(config.targetMarkerValuePath, null) : null;
  }

  hasRowProgressTargetMarker(row: any): boolean {
    return this.getRowProgressTargetMarkerValue(row) !== null;
  }

  getRowProgressTargetMarkerStyle(row: any): Record<string, string> {
    const config = this.getResolvedRowProgressConfig(row);
    const value = this.getRowProgressTargetMarkerValue(row);
    if (value === null) {
      return {};
    }
    const normalized = this.normalizeProgressMetricValue(
      value,
      config?.markerValueDivisor ?? config?.valueDivisor,
      config?.markerValueMultiplier ?? config?.valueMultiplier,
    );
    const dynamicSizes = this.getCachedDynamicProgressSegmentSizes(row, config);
    const percent = dynamicSizes
      ? this.mapValueToEqualThirdsPercent(normalized, dynamicSizes)
      : (this.resolveConfiguredProgressPercent(config, value, null) ?? 0);
    return {
      left: `${percent}%`,
      ...(this.isProgressLegendShown(row) ? { background: String(config?.targetMarkerLineColor || '#0f766e') } : {}),
    };
  }

  getRowProgressTargetMarkerValueStyle(row: any): Record<string, string> {
    const config = this.getResolvedRowProgressConfig(row);
    const offsetY = Number(config?.targetMarkerValueOffsetY);
    return {
      ...this.getRowProgressTargetMarkerStyle(row),
      ...(Number.isFinite(offsetY) ? { top: `${offsetY}px` } : {}),
    };
  }

  getRowProgressTargetMarkerText(row: any): string {
    const config = this.getResolvedRowProgressConfig(row);
    const value = this.getRowProgressTargetMarkerValue(row);
    if (value === null) {
      return '';
    }
    return this.formatProgressValueLabel(value, {
      suffix: config?.markerValueSuffix ?? config?.valueSuffix ?? '%',
      divisor: config?.markerValueDivisor ?? config?.valueDivisor,
      multiplier: config?.markerValueMultiplier ?? config?.valueMultiplier,
      decimals: config?.markerValueDecimals ?? config?.valueDecimals,
      prefix: config?.markerValuePrefix ?? config?.valuePrefix,
      useGrouping: config?.markerValueUseGrouping ?? config?.valueUseGrouping,
      compact: config?.markerValueCompact ?? config?.valueCompact,
    });
  }

  getRowProgressTargetMarkerLabel(row: any): string {
    const config = this.getResolvedRowProgressConfig(row);
    const text = this.getRowProgressTargetMarkerText(row);
    return text && config?.targetMarkerLabel ? `${config.targetMarkerLabel} ${text}` : text;
  }

  getRowProgressFooterLabel(row: any): string {
    const config = this.getResolvedRowProgressConfig(row);
    return String(config?.footerLabel || '').trim();
  }

  getDisplayStatus(row: any): string {
    return this.toDisplayText(row?.status);
  }

  private getRowProgressConfig(): any {
    return (this.data as any)?.rowProgress || (this.data as any)?.progressBar || null;
  }

  private resolveProgressLabels(config: any): Set<string> {
    const configured = config?.labels || config?.rows || config?.showForLabels || [];
    return new Set(
      (Array.isArray(configured) ? configured : [])
        .map((label: any) => this.normalizeRowLabel(label))
        .filter(Boolean),
    );
  }

  private resolveRowProgressPercent(row: any): number {
    const config = this.getResolvedRowProgressConfig(row);
    const forceNumericPosition = this.shouldForceNumericProgressPosition(config, row);
    const useStatusPosition = !forceNumericPosition && (
      config?.useStatusPosition === true || String(config?.positionSource || '').trim().toLowerCase() === 'status'
    );
    // The marker's on-screen POSITION can be driven by a different field than the value shown
    // in its label (e.g. position from "margin_contribution", displayed value from "margin_2025").
    // positionValuePath/Key opts into that; rows that don't set it keep using markerValuePath/Key
    // for both, exactly as before.
    const hasDistinctPositionValue = Boolean(config?.positionValuePath || config?.positionValueKey);
    const configuredPositionValue = hasDistinctPositionValue
      ? this.resolveConfiguredProgressValue(config?.positionValuePath, config?.positionValueKey)
      : this.resolveConfiguredProgressValue(config?.markerValuePath, config?.markerValueKey);
    const positionValueScaling = hasDistinctPositionValue
      ? { divisor: config?.positionValueDivisor ?? config?.valueDivisor, multiplier: config?.positionValueMultiplier ?? config?.valueMultiplier }
      : { divisor: config?.markerValueDivisor ?? config?.valueDivisor, multiplier: config?.markerValueMultiplier ?? config?.valueMultiplier };
    const configuredReferenceValue = this.resolveConfiguredProgressValue(config?.referenceValuePath, config?.referenceValueKey);
    const statusKey = String(config?.statusKey || config?.progressStatusKey || '').trim();
    const configuredStatus = this.resolveConfiguredProgressText(config?.statusValuePath || config?.statusPath, config?.statusValueKey);
    const rowStatus = statusKey ? row?.[statusKey] : undefined;
    const resolvedStatus = String(
      configuredStatus ||
      rowStatus ||
      row?.progressStatus ||
      row?.progress_status ||
      row?.status ||
      '',
    ).trim().toLowerCase();
    // When weakEnd/fairEnd are dynamic, the color bands are drawn as equal thirds (see
    // getRowProgressTrackStyle) rather than at their true numeric widths — so the marker must be
    // positioned through that same equal-thirds mapping, or it would land in the wrong-colored band.
    const dynamicSizes = useStatusPosition ? null : this.getCachedDynamicProgressSegmentSizes(row, config);
    const configuredPercent = useStatusPosition
      ? null
      : dynamicSizes && configuredPositionValue !== null
        ? this.mapValueToEqualThirdsPercent(
            this.normalizeProgressMetricValue(configuredPositionValue, positionValueScaling.divisor, positionValueScaling.multiplier),
            dynamicSizes,
          )
        : this.resolveConfiguredProgressPercent(config, configuredPositionValue, configuredReferenceValue, positionValueScaling);
    if (configuredPercent !== null) {
      return this.applyMinimumNonZeroProgressPercent(configuredPercent, configuredPositionValue, config, row);
    }

    const valueKey = String(config?.valueKey || config?.progressValueKey || '').trim();
    const rawValue = valueKey ? row?.[valueKey] : undefined;
    const numericValue = Number(rawValue);

    if (!useStatusPosition && Number.isFinite(numericValue)) {
      const min = Number(config?.min ?? 0);
      const max = Number(config?.max ?? 100);
      const safeMin = Number.isFinite(min) ? min : 0;
      const safeMax = Number.isFinite(max) && max > safeMin ? max : 100;
      return Math.max(0, Math.min(((numericValue - safeMin) / (safeMax - safeMin)) * 100, 100));
    }

    const statusPositions = {
      weak: Number(config?.weakPosition ?? 12.5),
      fair: Number(config?.fairPosition ?? 37.5),
      good: Number(config?.goodPosition ?? 83.5),
    };

    if (resolvedStatus === 'weak') {
      return statusPositions.weak;
    }

    if (resolvedStatus === 'fair') {
      return statusPositions.fair;
    }

    if (resolvedStatus === 'good') {
      return statusPositions.good;
    }

    return Number(config?.defaultPosition ?? 50);
  }

  private shouldForceNumericProgressPosition(config: any, row: any): boolean {
    const configuredCustomers = Array.isArray(config?.numericPositionCustomers)
      ? config.numericPositionCustomers
      : [];
    if (!configuredCustomers.length) {
      return false;
    }

    const activeCustomer = this.resolveActiveProgressCustomerName(row);
    if (!activeCustomer) {
      return false;
    }

    return configuredCustomers.some((customer: any) => this.normalizeCustomerName(customer) === activeCustomer);
  }

  private applyMinimumNonZeroProgressPercent(percent: number, markerValue: number | null, config: any, row: any): number {
    if (!Number.isFinite(percent) || markerValue === null || !Number.isFinite(markerValue) || markerValue <= 0) {
      return percent;
    }

    const configuredMinimum = Number(config?.minimumNonZeroPercent);
    if (!Number.isFinite(configuredMinimum) || configuredMinimum <= 0) {
      return percent;
    }

    return Math.max(percent, Math.min(100, configuredMinimum));
  }

  private resolveActiveProgressCustomerName(row: any): string {
    const responseSource = this.activeHoverConfig?.responseData;
    const responseCustomer =
      this.resolveConfiguredProgressText('matchedEngineLevel.customer_name', '') ||
      this.resolveConfiguredProgressText('responseList.0.customer_level_screen.0.customer_name', '') ||
      this.resolveConfiguredProgressText('responseList.0.data.0.customer_name', '');
    const rowCustomer = String(row?.customer_name || row?.customer || '').trim();
    const requestCustomer = String(this.activeHoverRequestParams?.['customer_name'] || '').trim();
    return this.normalizeCustomerName(responseCustomer || rowCustomer || requestCustomer || this.findValueForKey(responseSource, 'customer_name'));
  }

  private normalizeCustomerName(value: any): string {
    return String(value || '').trim().toLowerCase();
  }

  private adjustBoundaryPercentForStatus(percent: number, status: string): number {
    if (!Number.isFinite(percent)) {
      return percent;
    }

    const epsilon = 0.2;
    if (status === 'good') {
      return Math.min(100, percent + epsilon);
    }

    if (status === 'weak') {
      return Math.max(0, percent - epsilon);
    }

    return percent;
  }

  private resolveRowProgressReferencePercent(row: any): number {
    const config = this.getResolvedRowProgressConfig(row);
    const explicit = Number(config?.referencePosition);
    if (Number.isFinite(explicit)) {
      return Math.max(0, Math.min(100, explicit));
    }

    // fairEnd always sits at the boundary between the 2nd and 3rd equal-thirds band drawn by
    // getRowProgressTrackStyle (see mapValueToEqualThirdsPercent).
    const dynamicSizes = this.getCachedDynamicProgressSegmentSizes(row, config);
    if (dynamicSizes) {
      return 200 / 3;
    }

    return 50;
  }

  private resolveRowProgressSecondaryReferencePercent(row: any): number {
    const config = this.getResolvedRowProgressConfig(row);
    const explicit = Number(config?.secondaryReferencePosition);
    if (Number.isFinite(explicit)) {
      return Math.max(0, Math.min(100, explicit));
    }

    // weakEnd always sits at the boundary between the 1st and 2nd equal-thirds band.
    const dynamicSizes = this.getCachedDynamicProgressSegmentSizes(row, config);
    if (dynamicSizes) {
      return 100 / 3;
    }

    return 0;
  }

  private getResolvedRowProgressConfig(row: any): any {
    const baseConfig = this.getRowProgressConfig() || {};
    const rowKey = this.normalizeRowLabel(row?.originalLabel || row?.label);
    const overrides = baseConfig?.rowOverrides && typeof baseConfig.rowOverrides === 'object'
      ? baseConfig.rowOverrides
      : {};

    const matchingEntry = Object.entries(overrides).find(([key]) => this.normalizeRowLabel(key) === rowKey);
    return matchingEntry ? { ...baseConfig, ...(matchingEntry[1] as any) } : baseConfig;
  }

  updateHoverChartPosition(row: any, event: MouseEvent): void {
    if (!this.activeHoverConfig || this.activeHoverRow !== row || !this.shouldFollowCursor(this.activeHoverConfig)) {
      return;
    }

    this.activeHoverCursorPoint = { x: event.clientX, y: event.clientY };
    this.activeHoverAnchorRect = this.getHoverAnchorRect(event, this.activeHoverConfig);
    this.activeHoverStyle = this.buildHoverStyle(event, this.activeHoverConfig);
    this.scheduleAutoSizedHoverPosition();
  }

  private buildHoverStyle(event?: MouseEvent, chartConfig?: any): Record<string, string> {
    const viewportWidth = window.innerWidth || 1440;
    const viewportHeight = window.innerHeight || 900;
    const margin = 16;
    const isTooltip = this.normalizeHoverType(chartConfig?.type) === 'tooltip';
    const autoSize = isTooltip && (chartConfig?.popupAutoSize === true || String(chartConfig?.popupHeight || '').toLowerCase() === 'auto');
    const configuredWidth = Number(chartConfig?.popupWidth ?? chartConfig?.width);
    const preferredWidth = Number.isFinite(configuredWidth) ? configuredWidth : (isTooltip ? 260 : 560);
    const configuredHeight = Number(chartConfig?.popupHeight ?? chartConfig?.heightPx);
    const preferredHeight = Number.isFinite(configuredHeight) ? configuredHeight : (isTooltip ? 120 : 250);
    const minWidth = Number(chartConfig?.popupMinWidth ?? (isTooltip ? 220 : 220));
    const minHeight = Number(chartConfig?.popupMinHeight ?? (isTooltip ? 88 : 96));
    const maxWidth = Math.max(
      minWidth,
      Math.min(Number(chartConfig?.popupMaxWidth ?? preferredWidth), viewportWidth - (margin * 2)),
    );
    const maxHeight = Math.max(
      minHeight,
      Math.min(Number(chartConfig?.popupMaxHeight ?? (isTooltip ? 240 : preferredHeight)), viewportHeight - (margin * 2)),
    );
    const width = Math.max(minWidth, Math.min(preferredWidth, viewportWidth - (margin * 2)));
    const height = Math.max(minHeight, Math.min(preferredHeight, viewportHeight - (margin * 2)));
    const positionWidth = autoSize ? maxWidth : width;
    const positionHeight = autoSize ? maxHeight : height;
    const offsetX = Number(chartConfig?.popupOffsetX ?? 0);
    const offsetY = Number(chartConfig?.popupOffsetY ?? 0);
    const gap = Number(chartConfig?.popupGap ?? 8);
    const placement = String(chartConfig?.popupPlacement || 'auto').trim().toLowerCase();
    const target = event ? this.getHoverAnchorRect(event, chartConfig) : null;

    if (!target) {
      return {
        ...(autoSize
          ? {
              width: 'fit-content',
              'min-width': `${minWidth}px`,
              'max-width': `${maxWidth}px`,
              'min-height': `${minHeight}px`,
              'max-height': `${maxHeight}px`,
            }
          : {
              width: `${width}px`,
              height: `${height}px`,
            }),
        left: `${Math.max(margin, Math.round((viewportWidth - positionWidth) / 2))}px`,
        top: `${Math.max(margin, Math.round((viewportHeight - positionHeight) / 2))}px`,
      };
    }

    const spaceOnLeft = target.left - margin;
    const spaceOnRight = viewportWidth - target.right - margin;
    const autoLeft = spaceOnLeft >= positionWidth
      ? target.left - positionWidth - gap
      : spaceOnRight >= positionWidth
        ? target.right + gap
        : Math.max(margin, Math.min(target.left - ((positionWidth - target.width) / 2), viewportWidth - positionWidth - margin));
    const rightLeft = spaceOnRight >= positionWidth
      ? target.right + gap
      : spaceOnLeft >= positionWidth
        ? target.left - positionWidth - gap
        : Math.max(margin, Math.min(target.right - 24, viewportWidth - positionWidth - margin));
    const leftLeft = spaceOnLeft >= positionWidth
      ? target.left - positionWidth - gap
      : spaceOnRight >= positionWidth
        ? target.right + gap
        : Math.max(margin, Math.min(target.left - ((positionWidth - target.width) / 2), viewportWidth - positionWidth - margin));
    const left = placement === 'right'
      ? rightLeft
      : placement === 'above' || placement === 'below'
        ? Math.max(
            margin,
            Math.min(
              target.left + (target.width / 2) - (positionWidth / 2),
              viewportWidth - positionWidth - margin,
            ),
          )
      : placement === 'left'
        ? leftLeft
        : autoLeft;
    const centeredTop = Math.max(
      margin,
      Math.min(
        target.top + (target.height / 2) - (positionHeight / 2),
        viewportHeight - positionHeight - margin,
      ),
    );
    const aboveTop = target.top - positionHeight - gap;
    const belowTop = target.top + target.height + gap;
    const top = placement === 'above'
      ? Math.max(margin, aboveTop)
      : placement === 'below'
        ? Math.max(margin, belowTop)
        : centeredTop;

    return {
      ...(autoSize
        ? {
            width: 'fit-content',
            'min-width': `${Math.round(minWidth)}px`,
            'max-width': `${Math.round(maxWidth)}px`,
            'min-height': `${Math.round(minHeight)}px`,
            'max-height': `${Math.round(maxHeight)}px`,
          }
        : {
            width: `${Math.round(width)}px`,
            height: `${Math.round(height)}px`,
          }),
      left: `${Math.round(left + offsetX)}px`,
      top: `${Math.round(top + offsetY)}px`,
      ...(chartConfig?.popupPadding ? { padding: String(chartConfig.popupPadding) } : {}),
      ...(chartConfig?.popupBorderRadius ? { 'border-radius': String(chartConfig.popupBorderRadius) } : {}),
      ...(chartConfig?.popupBoxShadow ? { 'box-shadow': String(chartConfig.popupBoxShadow) } : {}),
    };
  }

  private normalizeKey(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'row';
  }

  private getUnavailableNote(chartConfig: any): string {
    const configuredNote = String(chartConfig?.unavailableNote || chartConfig?.dataUnavailableNote || '').trim();
    return configuredNote || AxisDriversComponent.UNAVAILABLE_NOTE;
  }

  private getHoverAnchorRect(event: MouseEvent, chartConfig?: any): { left: number; right: number; top: number; width: number; height: number } | null {
    const anchor = String(chartConfig?.popupAnchor || 'row').trim().toLowerCase();
    const currentTarget = event.currentTarget instanceof HTMLElement ? event.currentTarget : null;
    const labelElement = currentTarget?.classList.contains('axis-drivers__label')
      ? currentTarget
      : currentTarget?.querySelector('.axis-drivers__label') as HTMLElement | null;
    const labelRect = labelElement?.getBoundingClientRect();
    if (anchor === 'cursor') {
      return {
        left: Math.max(labelRect?.left ?? event.clientX, Math.min(event.clientX, labelRect?.right ?? event.clientX)),
        right: Math.max(labelRect?.left ?? event.clientX, Math.min(event.clientX, labelRect?.right ?? event.clientX)),
        top: labelRect?.bottom ?? event.clientY,
        width: 0,
        height: 0,
      };
    }

    if (anchor === 'label' && labelRect) {
      return {
        left: labelRect.left,
        right: labelRect.right,
        top: labelRect.top,
        width: labelRect.width,
        height: labelRect.height,
      };
    }

    const anchorElement = currentTarget && anchor === 'badge'
      ? currentTarget.querySelector('.axis-drivers__badge') as HTMLElement | null
      : currentTarget;
    const rect = anchorElement?.getBoundingClientRect();

    return rect
      ? {
          left: rect.left,
          right: rect.right,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        }
      : null;
  }

  private scheduleAutoSizedHoverPosition(): void {
    if (!this.isAutoSizedTooltip(this.activeHoverConfig)) {
      return;
    }

    window.requestAnimationFrame(() => this.repositionAutoSizedHover());
  }

  private repositionAutoSizedHover(): void {
    if (!this.isAutoSizedTooltip(this.activeHoverConfig) || !this.activeHoverStyle || !this.activeHoverAnchorRect || !this.hoverCard?.nativeElement) {
      return;
    }

    const viewportWidth = window.innerWidth || 1440;
    const viewportHeight = window.innerHeight || 900;
    const margin = 16;
    const gap = Number(this.activeHoverConfig?.popupGap ?? 8);
    const offsetX = Number(this.activeHoverConfig?.popupOffsetX ?? 0);
    const offsetY = Number(this.activeHoverConfig?.popupOffsetY ?? 0);
    const placement = String(this.activeHoverConfig?.popupPlacement || 'auto').trim().toLowerCase();
    const actualWidth = Math.min(
      this.hoverCard.nativeElement.offsetWidth,
      viewportWidth - (margin * 2),
    );
    const actualHeight = Math.min(
      this.hoverCard.nativeElement.offsetHeight,
      viewportHeight - (margin * 2),
    );
    const spaceOnLeft = this.activeHoverAnchorRect.left - margin;
    const spaceOnRight = viewportWidth - this.activeHoverAnchorRect.right - margin;
    const autoLeft = spaceOnLeft >= actualWidth
      ? this.activeHoverAnchorRect.left - actualWidth - gap
      : spaceOnRight >= actualWidth
        ? this.activeHoverAnchorRect.right + gap
        : Math.max(
            margin,
            Math.min(
              this.activeHoverAnchorRect.left + (this.activeHoverAnchorRect.width / 2) - (actualWidth / 2),
              viewportWidth - actualWidth - margin,
            ),
          );
    const left = placement === 'right'
      ? (spaceOnRight >= actualWidth
          ? this.activeHoverAnchorRect.right + gap
          : Math.max(
              margin,
              Math.min(
                this.activeHoverAnchorRect.left + (this.activeHoverAnchorRect.width / 2) - (actualWidth / 2),
                viewportWidth - actualWidth - margin,
              ),
            ))
      : placement === 'left'
        ? (spaceOnLeft >= actualWidth
            ? this.activeHoverAnchorRect.left - actualWidth - gap
            : Math.max(
                margin,
                Math.min(
                  this.activeHoverAnchorRect.left + (this.activeHoverAnchorRect.width / 2) - (actualWidth / 2),
                  viewportWidth - actualWidth - margin,
                ),
              ))
        : Math.max(
            margin,
            Math.min(
              placement === 'above' || placement === 'below'
                ? this.activeHoverAnchorRect.left + (this.activeHoverAnchorRect.width / 2) - (actualWidth / 2)
                : autoLeft,
              viewportWidth - actualWidth - margin,
            ),
          );
    const centeredTop = Math.max(
      margin,
      Math.min(
        this.activeHoverAnchorRect.top + (this.activeHoverAnchorRect.height / 2) - (actualHeight / 2),
        viewportHeight - actualHeight - margin,
      ),
    );
    const aboveTop = this.activeHoverAnchorRect.top - actualHeight - gap;
    const belowTop = this.activeHoverAnchorRect.top + this.activeHoverAnchorRect.height + gap;
    const top = placement === 'above'
      ? Math.max(margin, aboveTop)
      : placement === 'below'
        ? Math.max(margin, belowTop)
        : centeredTop;

    this.activeHoverStyle = {
      ...this.activeHoverStyle,
      left: `${Math.round(left + offsetX)}px`,
      top: `${Math.round(top + offsetY)}px`,
    };
  }

  private isAutoSizedTooltip(chartConfig?: any): boolean {
    return this.normalizeHoverType(chartConfig?.type) === 'tooltip' &&
      (chartConfig?.popupAutoSize === true || String(chartConfig?.popupHeight || '').toLowerCase() === 'auto');
  }

  private shouldFollowCursor(chartConfig?: any): boolean {
    return String(chartConfig?.popupAnchor || '').trim().toLowerCase() === 'cursor' || chartConfig?.popupFollowCursor === true;
  }

  private isLabelOnlyHover(row: any): boolean {
    const chartConfig = this.getHoverChartConfig(row);
    return String(chartConfig?.popupTrigger || '').trim().toLowerCase() === 'label';
  }

  private normalizeHoverType(value: any): string {
    return String(value || '').trim().toLowerCase();
  }

  private toDisplayText(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).trim();
  }

  private applyConfiguredRowOrder(): void {
    const configuredOrder = this.getConfiguredRowOrder();
    const labelMap = this.getConfiguredRowLabelMap();
    const hiddenRows = this.getConfiguredHiddenRows();
    if ((!configuredOrder.length && labelMap.size === 0 && hiddenRows.size === 0) || !this.rows?.length) {
      return;
    }

    const orderIndex = new Map(
      configuredOrder.map((label, index) => [this.normalizeRowLabel(label), index]),
    );
    const currentSignature = JSON.stringify({
      order: configuredOrder.map((label) => this.normalizeRowLabel(label)),
      labels: Array.from(labelMap.entries()),
      hidden: Array.from(hiddenRows),
      rows: this.rows.map((row: any) => ({
        label: this.normalizeRowLabel(row?.label),
        originalLabel: this.normalizeRowLabel(row?.originalLabel),
      })),
    });

    if (currentSignature === this.lastRowOrderSignature) {
      return;
    }

    const sortedRows = this.rows
      .filter((row: any) =>
        !hiddenRows.has(this.normalizeRowLabel(row?.originalLabel || row?.label)) &&
        !hiddenRows.has(this.normalizeRowLabel(row?.label)),
      )
      .map((row: any, index: number) => {
        const originalLabel = row?.originalLabel || row?.label;
        const displayLabel =
          labelMap.get(this.normalizeRowLabel(originalLabel)) ||
          labelMap.get(this.normalizeRowLabel(row?.label)) ||
          row?.label;

        return {
          row: {
            ...row,
            originalLabel,
            label: displayLabel,
          },
          index,
        };
      })
      .sort((left, right) => {
        const leftOrder =
          orderIndex.get(this.normalizeRowLabel(left.row?.label)) ??
          orderIndex.get(this.normalizeRowLabel(left.row?.originalLabel));
        const rightOrder =
          orderIndex.get(this.normalizeRowLabel(right.row?.label)) ??
          orderIndex.get(this.normalizeRowLabel(right.row?.originalLabel));
        const leftRank = leftOrder ?? Number.MAX_SAFE_INTEGER;
        const rightRank = rightOrder ?? Number.MAX_SAFE_INTEGER;

        if (leftRank !== rightRank) {
          return leftRank - rightRank;
        }

        return left.index - right.index;
      })
      .map(({ row }) => row);

    const sortedSignature = JSON.stringify({
      order: configuredOrder.map((label) => this.normalizeRowLabel(label)),
      labels: Array.from(labelMap.entries()),
      hidden: Array.from(hiddenRows),
      rows: sortedRows.map((row: any) => ({
        label: this.normalizeRowLabel(row?.label),
        originalLabel: this.normalizeRowLabel(row?.originalLabel),
      })),
    });

    this.lastRowOrderSignature = sortedSignature;
    this.rows = sortedRows;
  }

  private getConfiguredRowOrder(): string[] {
    const order = (this.data as any)?.rowOrder || (this.data as any)?.labelOrder || (this.data as any)?.order;
    return Array.isArray(order)
      ? order.map((label) => String(label || '').trim()).filter(Boolean)
      : [];
  }

  private getConfiguredHiddenRows(): Set<string> {
    const hidden = (this.data as any)?.hiddenRows;
    return new Set(
      Array.isArray(hidden)
        ? hidden.map((label) => this.normalizeRowLabel(label)).filter(Boolean)
        : [],
    );
  }

  private getConfiguredRowLabelMap(): Map<string, string> {
    const labels = (this.data as any)?.rowLabels || (this.data as any)?.labelMap || (this.data as any)?.displayLabels;
    const entries = labels && typeof labels === 'object' && !Array.isArray(labels)
      ? Object.entries(labels)
      : [];

    return new Map(
      entries
        .map(([source, display]) => [this.normalizeRowLabel(source), String(display || '').trim()] as [string, string])
        .filter(([, display]) => Boolean(display)),
    );
  }

  private normalizeRowLabel(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\([^)]*\)/g, ' ')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private resolveProgressSegmentSize(value: any, fallback: number): number {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallback;
  }

  private resolveConfiguredProgressValue(path: any, key: any): number | null {
    const source = this.activeHoverConfig?.responseData;
    const directPath = String(path || '').trim();
    const directKey = String(key || '').trim();
    const rawValue = directPath
      ? this.resolveConfiguredProgressPath(source, directPath)
      : directKey
        ? this.resolveConfiguredProgressPath(source, directKey)
        : undefined;
    const numericValue = Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private resolveConfiguredProgressText(path: any, key: any): string {
    return this.resolveConfiguredProgressTextFromSource(this.activeHoverConfig?.responseData, path, key);
  }

  private resolveConfiguredProgressTextFromSource(source: any, path: any, key: any): string {
    const directPath = String(path || '').trim();
    const directKey = String(key || '').trim();
    const rawValue = directPath
      ? this.resolveConfiguredProgressPath(source, directPath)
      : directKey
        ? this.resolveConfiguredProgressPath(source, directKey)
        : undefined;

    return rawValue === null || rawValue === undefined ? '' : String(rawValue).trim();
  }

  private resolveConfiguredProgressPath(source: any, path: string): any {
    const normalizedPath = String(path || '').trim();
    const enginePrefix = 'matchedEngineLevel.';
    if (normalizedPath.startsWith(enginePrefix)) {
      const matchedRow = this.resolveMatchedEngineLevelRow(source);
      return this.resolveHoverPath(matchedRow, normalizedPath.slice(enginePrefix.length));
    }

    return this.resolveHoverPath(source, normalizedPath);
  }

  private resolveMatchedEngineLevelRow(source: any): any {
    const rows = this.resolveHoverPath(source, 'responseList.0.engine_level_screen');
    if (!Array.isArray(rows) || !rows.length) {
      return null;
    }

    const requestParams = this.activeHoverRequestParams || {};
    const exactKeys = ['customer_name', 'engine_model', 'application', 'certification'];
    const normalize = (value: any) => String(value ?? '').trim().toLowerCase();
    const hasMatchValue = (key: string) => normalize(requestParams[key]) !== '';
    const matchesKey = (row: any, key: string) => {
      const rowValue = normalize(row?.[key]);
      return !hasMatchValue(key) || !rowValue || rowValue === normalize(requestParams[key]);
    };
    const exactMatch = rows.find((row) => exactKeys.every((key) => matchesKey(row, key)));

    if (exactMatch) {
      return exactMatch;
    }

    const partialKeys = ['customer_name', 'engine_model'];
    return rows.find((row) => partialKeys.every((key) => matchesKey(row, key))) || rows[0];
  }

  private resolveMatchedCustomerSalesRow(source: any): any {
    const rows = this.resolveHoverPath(source, 'responseList.0.customer_sales');
    if (!Array.isArray(rows) || !rows.length) {
      return null;
    }

    const engineRow = this.resolveMatchedEngineLevelRow(source);
    if (!engineRow) {
      return rows[0];
    }

    const isClose = (left: any, right: any) => {
      const leftValue = Number(left);
      const rightValue = Number(right);
      if (!Number.isFinite(leftValue) || !Number.isFinite(rightValue)) {
        return false;
      }

      return Math.abs(leftValue - rightValue) < 0.01;
    };

    return (
      rows.find((row) =>
        isClose(row?.sales_2025, engineRow?.sales_2025) &&
        isClose(row?.sales_grade_1, engineRow?.sales_grade_1) &&
        isClose(row?.sales_grade_2, engineRow?.sales_grade_2) &&
        isClose(row?.sales_grade_3, engineRow?.sales_grade_3),
      ) ||
      rows.find((row) => isClose(row?.sales_2025, engineRow?.sales_2025)) ||
      rows[0]
    );
  }

  private resolveConfiguredProgressPercent(
    config: any,
    markerValue: number | null,
    referenceValue: number | null,
    valueScaling?: { divisor?: any; multiplier?: any },
  ): number | null {
    if (markerValue === null) {
      return null;
    }

    const normalizedMarkerValue = this.normalizeProgressMetricValue(
      markerValue,
      valueScaling?.divisor ?? config?.markerValueDivisor ?? config?.valueDivisor,
      valueScaling?.multiplier ?? config?.markerValueMultiplier ?? config?.valueMultiplier,
    );

    const scaleMode = String(config?.valueScaleMode || '').trim().toLowerCase();

    // delta-from-reference has its own explicit min/max/reference formula below; scaleLabels in
    // that mode are axis-display labels only; treating them as an interpolation table (like sales'
    // grade breakpoints do) would let a marker value beyond the labels' range collapse onto the
    // last label's position instead of extrapolating correctly relative to the reference.
    if (scaleMode !== 'delta-from-reference') {
      const scaledPercent = this.resolveConfiguredScaleLabelPercent(config, normalizedMarkerValue);
      if (scaledPercent !== null) {
        return scaledPercent;
      }
    }

    const configuredMin = this.resolveConfiguredProgressValue(config?.minValuePath, config?.minValueKey);
    const configuredMax = this.resolveConfiguredProgressValue(config?.maxValuePath, config?.maxValueKey);
    const min = Number(this.normalizeProgressMetricValue(
      configuredMin ?? config?.min ?? config?.rangeMin ?? -50,
      config?.minValueDivisor ?? config?.valueDivisor,
      config?.minValueMultiplier ?? config?.valueMultiplier,
    ));
    const max = Number(this.normalizeProgressMetricValue(
      configuredMax ?? config?.max ?? config?.rangeMax ?? 50,
      config?.maxValueDivisor ?? config?.valueDivisor,
      config?.maxValueMultiplier ?? config?.valueMultiplier,
    ));
    const safeMin = Number.isFinite(min) ? min : -50;
    const safeMax = Number.isFinite(max) && max > safeMin ? max : 50;

    // delta-from-reference used to force the reference value to sit at a fixed 50% (center),
    // scaling each side independently — but the color bands (weakEndValue/fairEndValuePath) are
    // positioned with plain absolute linear scaling against the same min/max. When the reference
    // isn't the scale's midpoint (e.g. fair_margin far from 0), those two coordinate systems
    // disagreed: a marker value above the reference could still land visually left of the
    // yellow/green boundary. Using the same absolute linear formula as the bands keeps them
    // consistent — a value above the reference now always lands past the reference's own
    // position on the bar, matching the color it's drawn on.
    return Math.max(0, Math.min(((normalizedMarkerValue - safeMin) / (safeMax - safeMin)) * 100, 100));
  }

  private resolveConfiguredScaleLabelPercent(config: any, markerValue: number): number | null {
    type ProgressScalePosition = {
      value: number;
      position: number;
    };

    const labels = (Array.isArray(config?.scaleLabels) ? config.scaleLabels : [])
      .map((label: any) => {
        // Literal-text labels (e.g. static "-50%"/"50%" edge labels with no valuePath/valueKey)
        // have no real numeric value — resolveConfiguredProgressValue correctly returns null for
        // them. They must be excluded here rather than coerced to 0 (Number(null) === 0), or they
        // corrupt this interpolation table used to position the actual marker.
        const literalValue = Number(label?.value);
        const value = Number.isFinite(literalValue)
          ? literalValue
          : this.resolveConfiguredProgressValue(label?.valuePath, label?.valueKey);
        if (value === null) {
          return null;
        }

        const numericValue = Number(this.normalizeProgressMetricValue(
          value,
          label?.divisor ?? config?.valueDivisor,
          label?.multiplier ?? config?.valueMultiplier,
        ));
        const position = Number(label?.position);
        if (!Number.isFinite(numericValue) || !Number.isFinite(position)) {
          return null;
        }

        return {
          value: numericValue,
          position: Math.max(0, Math.min(100, position)),
        };
      })
      .filter((label: ProgressScalePosition | null): label is ProgressScalePosition => Boolean(label))
      .sort((left: ProgressScalePosition, right: ProgressScalePosition) => left.value - right.value);

    if (!labels.length) {
      return null;
    }

    if (markerValue <= labels[0].value) {
      return labels[0].position;
    }

    for (let index = 1; index < labels.length; index += 1) {
      const previous = labels[index - 1];
      const current = labels[index];
      if (Math.abs(markerValue - previous.value) < 0.0001) {
        return previous.position;
      }
      if (Math.abs(markerValue - current.value) < 0.0001) {
        return current.position;
      }
      if (markerValue > current.value) {
        continue;
      }

      const span = current.value - previous.value;
      if (span <= 0) {
        return current.position;
      }

      const ratio = (markerValue - previous.value) / span;
      return previous.position + (current.position - previous.position) * ratio;
    }

    return labels[labels.length - 1].position;
  }

  private resolveProgressValueDecimals(value: any): number {
    const decimals = Number(value);
    return Number.isFinite(decimals) && decimals >= 0 ? decimals : 2;
  }

  private roundProgressValue(value: number, decimals: number): number {
    return Number(value.toFixed(decimals));
  }

  private normalizeProgressMetricValue(value: any, divisor?: any, multiplier?: any): number {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return Number.NaN;
    }

    const numericDivisor = Number(divisor);
    if (Number.isFinite(numericDivisor) && numericDivisor !== 0) {
      return numericValue / numericDivisor;
    }

    const numericMultiplier = Number(multiplier);
    if (Number.isFinite(numericMultiplier)) {
      return numericValue * numericMultiplier;
    }

    return numericValue;
  }

  private formatProgressValueLabel(
    value: number | null,
    options: { suffix?: any; divisor?: any; multiplier?: any; decimals?: any; prefix?: any; useGrouping?: any; compact?: any },
  ): string {
    if (value === null) {
      return '';
    }

    const divisor = Number(options?.divisor);
    const multiplier = Number(options?.multiplier);
    const decimals = Number(options?.decimals);
    let displayValue = value;

    if (Number.isFinite(divisor) && divisor !== 0) {
      displayValue = value / divisor;
    } else if (Number.isFinite(multiplier)) {
      displayValue = value * multiplier;
    }

    const safeDecimals = Number.isFinite(decimals) && decimals >= 0 ? decimals : 2;
    const normalizedSuffix = String(options?.suffix || '').trim();
    const absoluteValue = Math.abs(value);

    // Keep small non-zero sales values readable instead of collapsing them to 0M.
    if (normalizedSuffix === 'M' && Number.isFinite(divisor) && divisor === 1000000 && absoluteValue > 0 && Math.abs(displayValue) < 1) {
      if (absoluteValue >= 1000) {
        const thousandsValue = value / 1000;
        const thousandsDecimals = Math.abs(thousandsValue) >= 100 ? 0 : 1;
        return `${Number(thousandsValue.toFixed(thousandsDecimals)).toString()}K`;
      }

      return Number(value.toFixed(0)).toString();
    }

    // compact: round to K / M / B (e.g. $10.5K, $1.2M) instead of the full amount.
    if (options?.compact === true) {
      const units = [{ size: 1e9, label: 'B' }, { size: 1e6, label: 'M' }, { size: 1e3, label: 'K' }];
      const unit = units.find((candidate) => Math.abs(value) >= candidate.size);
      const scaled = unit ? value / unit.size : value;
      const compactText = `${Number(scaled.toFixed(Number.isFinite(decimals) && decimals >= 0 ? decimals : 1))}${unit ? unit.label : ''}`;
      return `${String(options?.prefix || '')}${compactText}${normalizedSuffix}`;
    }

    // prefix / useGrouping show a currency amount, e.g. $19,470 (a price, not a percentage).
    const formattedValue = options?.useGrouping === true
      ? displayValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: safeDecimals })
      : Number(displayValue.toFixed(safeDecimals)).toString();
    const prefixedValue = `${String(options?.prefix || '')}${formattedValue}`;
    return normalizedSuffix ? `${prefixedValue}${normalizedSuffix}` : prefixedValue;
  }

  private resolveConfiguredStatusLabel(row: any): string {
    if (this.activeHoverRow !== row || !this.activeHoverConfig?.responseData) {
      return '';
    }

    return this.resolveConfiguredStatusFromSource(row, this.activeHoverConfig?.responseData);
  }

  private resolveConfiguredStatusFromSource(row: any, source: any): string {
    const config = this.getResolvedRowProgressConfig(row);
    const configuredStatus = this.resolveConfiguredProgressTextFromSource(
      source,
      config?.statusBadgeValuePath || config?.statusValuePath || config?.statusPath,
      config?.statusBadgeValueKey || config?.statusValueKey,
    );
    const normalizedStatus = String(configuredStatus || '').trim();
    if (!normalizedStatus) {
      return '';
    }

    const lowerStatus = normalizedStatus.toLowerCase();
    if (lowerStatus === 'good' || lowerStatus === 'fair' || lowerStatus === 'weak') {
      return lowerStatus.charAt(0).toUpperCase() + lowerStatus.slice(1);
    }

    return normalizedStatus;
  }

  private clearHideHoverTimer(): void {
    if (!this.hideHoverTimer) {
      return;
    }

    clearTimeout(this.hideHoverTimer);
    this.hideHoverTimer = undefined;
  }

  private loadHoverChartData(chartConfig: any, row: any): void {
    const hoverType = this.normalizeHoverType(chartConfig?.type);
    if (
      !chartConfig?.apiUrl ||
      !['mixed-stackbar-chart', 'cagr-hover-chart', 'market-share-progress', 'treemap-chart', 'tooltip'].includes(hoverType)
    ) {
      return;
    }

    this.hoverLoadSub?.unsubscribe();
    const filters = this.buildHoverFilters(chartConfig, row);
    this.activeHoverRequestParams = { ...(filters?.requestParams || {}) };
    this.hoverLoadSub = this.hoverConfigService
      .getConfig(chartConfig.apiUrl, chartConfig, filters, chartConfig.method || 'post')
      .pipe(timeout(Number(chartConfig?.requestTimeoutMs ?? 15000)))
      .subscribe({
        next: (response) => {
          if (this.activeHoverRow !== row) {
            return;
          }

          if (hoverType === 'tooltip') {
            const tooltipValue = this.resolveHoverTooltipValue(response, chartConfig);
            this.hoverTooltipValue = tooltipValue === undefined || tooltipValue === null ? '' : String(tooltipValue);
            this.hoverTooltipError = this.hoverTooltipValue ? '' : 'Data unavailable';
            this.hoverUnavailableNote = this.hoverTooltipValue ? '' : this.getUnavailableNote(chartConfig);
            this.hoverTooltipLoading = false;
            this.scheduleAutoSizedHoverPosition();
            return;
          }

          const responsePatch = this.buildHoverResponsePatch(response, chartConfig);
          const hasHoverData = this.hasHoverResponseData(responsePatch, hoverType);

          this.hoverChartLoading = false;
          this.hoverChartError = hasHoverData ? '' : 'Data unavailable';
          this.hoverUnavailableNote = hasHoverData ? '' : this.getUnavailableNote(chartConfig);
          this.activeHoverConfig = {
            ...this.activeHoverConfig,
            ...responsePatch,
          };
          this.salesProgressBoundaryLabels = this.buildSalesProgressBoundaryLabels(response, row);
        },
        error: (error) => {
          this.salesProgressBoundaryLabels = [];
          if (hoverType === 'tooltip') {
            this.hoverTooltipValue = '';
            this.hoverTooltipLoading = false;
            this.hoverTooltipError = 'Unable to load details.';
            this.hoverUnavailableNote = this.getUnavailableNote(chartConfig);
          } else {
            this.hoverChartLoading = false;
            this.hoverChartError = 'Data unavailable';
            this.hoverUnavailableNote = this.getUnavailableNote(chartConfig);
          }
          console.error('[AxisDriversComponent] hover chart load failed:', error);
        },
      });
  }

  private hasHoverResponseData(responsePatch: any, hoverType: string): boolean {
    if (hoverType === 'market-share-progress') {
      return this.hasAvailableNumericValue(responsePatch?.value);
    }

    if (hoverType === 'treemap-chart') {
      const rows = this.resolveHoverPath(responsePatch?.responseData, this.activeHoverConfig?.flatRowsPath || 'responseList.0.salesforcewin');
      return Array.isArray(rows) && rows.length > 0;
    }

    if (hoverType === 'cagr-hover-chart') {
      // A growth rate needs at least two years of sales; with one year there is nothing to compare,
      // so whatever CAGR value exists is not shown.
      return Number.isFinite(responsePatch?.progressValue)
        && Array.isArray(responsePatch?.data)
        && responsePatch.data.filter((row: any) => Number(row?.sales) > 0).length >= 2;
    }

    return Array.isArray(responsePatch?.data) && responsePatch.data.length > 0;
  }

  private hasAvailableNumericValue(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }

    return Number.isFinite(Number(value));
  }

  private buildHoverResponsePatch(response: any, chartConfig: any): any {
    if (chartConfig?.type === 'mixed-stackbar-chart') {
      return this.buildMixedPatchFromResponse(response, chartConfig);
    }

    if (chartConfig?.type === 'cagr-hover-chart') {
      return this.buildCagrPatchFromResponse(response, chartConfig);
    }

    if (chartConfig?.type === 'market-share-progress') {
      return this.buildMarketSharePatchFromResponse(response, chartConfig);
    }

    if (chartConfig?.type === 'treemap-chart') {
      return { responseData: response };
    }

    return { responseData: response };
  }

  private resolveHoverTooltipValue(response: any, chartConfig: any): any {
    if (chartConfig?.valuePath) {
      return this.resolveHoverPath(response, chartConfig.valuePath);
    }

    const source = chartConfig?.responsePath
      ? this.resolveHoverPath(response, chartConfig.responsePath)
      : response;

    if (chartConfig?.valueKey) {
      return this.resolveHoverPath(source, chartConfig.valueKey);
    }

    return source;
  }

  private buildHoverFilters(chartConfig: any, row: any): any {
    const inheritedFilters = (this as any).lastFilters || {};
    const currentFilters = this.hoverGlobalFilterService.getCurrentFilters?.() || {};
    const requestParams = {
      ...(inheritedFilters?.requestParams || {}),
      ...(currentFilters?.requestParams || {}),
      ...(chartConfig.defaultRequestData || {}),
      ...(chartConfig.requestData || {}),
    };

    for (const [key, value] of Object.entries(requestParams)) {
      if (value === '$row.label') {
        requestParams[key] = row?.label;
      } else if (value === '$row.originalLabel') {
        requestParams[key] = row?.originalLabel || row?.label;
      }
    }

    return {
      ...inheritedFilters,
      ...currentFilters,
      queryParams: {
        ...(inheritedFilters?.queryParams || {}),
        ...(currentFilters?.queryParams || {}),
      },
      requestParams,
    };
  }

  private buildMixedRowsFromResponse(response: any, chartConfig: any): any[] {
    const row = this.resolveHoverResponseRow(response, chartConfig);

    if (!row) {
      return [];
    }

    const years = Array.isArray(chartConfig.years)
      ? chartConfig.years
      : [2020, 2021, 2022, 2023, 2024, 2025];
    const salesPrefix = chartConfig.salesPrefix || 'sales_';
    const marginPrefix = chartConfig.marginPrefix || 'margin_';
    // Some responses store margin_<year> as a raw currency amount rather than a 0-1 fraction.
    // Multiplying those by 100 yields nonsense (e.g. 76872.95 -> 7,687,295%), which then clips
    // off the top of the axis and leaves the line looking like it only has a few 0% points.
    // marginAsRatioOfSales opts a row into deriving the percentage as margin/sales instead, so
    // it is correct regardless of which of the two shapes the backend sends.
    const marginAsRatioOfSales = chartConfig.marginAsRatioOfSales === true;

    return years.map((year: any) => {
      const rawSales = Number(row[`${salesPrefix}${year}`]);
      const rawMargin = Number(row[`${marginPrefix}${year}`]);
      const marginSource = marginAsRatioOfSales
        ? (Number.isFinite(rawMargin) && Number.isFinite(rawSales) && rawSales !== 0 ? rawMargin / rawSales : 0)
        : rawMargin;

      return {
        year: String(year),
        sales: this.toDisplayNumber(row[`${salesPrefix}${year}`], chartConfig.salesDivisor),
        margin: this.toDisplayNumber(marginSource, undefined, chartConfig.marginMultiplier),
      };
    });
  }

  private buildMixedPatchFromResponse(response: any, chartConfig: any): any {
    const row = this.resolveHoverResponseRow(response, chartConfig);
    if (!row) {
      return {
        data: [],
        responseData: response,
      };
    }

    const years = Array.isArray(chartConfig.years)
      ? chartConfig.years
      : [2020, 2021, 2022, 2023, 2024, 2025];
    const salesPrefix = chartConfig.salesPrefix || 'sales_';
    const marginPrefix = chartConfig.marginPrefix || 'margin_';
    const marginAsRatioOfSales = chartConfig.marginAsRatioOfSales === true;
    const salesUnit = this.resolveMixedChartSalesUnit(row, chartConfig, years, salesPrefix);

    const data = years.map((year: any) => {
      const rawSales = Number(row[`${salesPrefix}${year}`]);
      const rawMargin = Number(row[`${marginPrefix}${year}`]);
      const marginSource = marginAsRatioOfSales
        ? (Number.isFinite(rawMargin) && Number.isFinite(rawSales) && rawSales !== 0 ? rawMargin / rawSales : 0)
        : rawMargin;

      const sales = this.toDisplayNumber(row[`${salesPrefix}${year}`], salesUnit.divisor);
      const margin = this.toDisplayNumber(marginSource, undefined, chartConfig.marginMultiplier);
      // blankMissingYears keeps a year with no sales and no margin on the axis but plots nothing for it
      // (no 0% margin point), like an empty slot.
      const missingYear = chartConfig?.blankMissingYears === true && !(sales > 0) && !(margin > 0);

      return {
        year: String(year),
        sales,
        margin: missingYear ? null : margin,
      };
    });
    const filteredData = chartConfig?.showOnlyPresentData === true
      ? data.filter((entry: any) => this.shouldKeepMixedRow(entry))
      : data;
    const leftAlignedData = this.padMixedRowsForLeftAlignment(filteredData, years, chartConfig);

    return {
      data: leftAlignedData,
      responseData: response,
      barLabelSuffix: salesUnit.barLabelSuffix || '',
      ...(salesUnit.leftTitle ? { leftTitle: salesUnit.leftTitle } : {}),
    };
  }

  private shouldKeepMixedRow(entry: any): boolean {
    const sales = Number(entry?.sales);
    const margin = Number(entry?.margin);
    const hasSales = Number.isFinite(sales) && Math.abs(sales) > 1e-9;
    const hasMargin = Number.isFinite(margin) && Math.abs(margin) > 1e-9;
    return hasSales || hasMargin;
  }

  private padMixedRowsForLeftAlignment(rows: any[], years: any[], chartConfig: any): any[] {
    if (chartConfig?.leftAlignSparseData !== true) {
      return rows;
    }

    if (!Array.isArray(rows) || rows.length === 0) {
      return rows;
    }

    const targetSlotCount = Math.max(
      rows.length,
      Number.isFinite(Number(chartConfig?.sparseDataSlotCount))
        ? Number(chartConfig.sparseDataSlotCount)
        : (Array.isArray(years) ? years.length : rows.length),
    );
    if (!Number.isFinite(targetSlotCount) || targetSlotCount <= rows.length) {
      return rows;
    }

    const placeholders = Array.from({ length: targetSlotCount - rows.length }, () => ({
      year: '',
      sales: null,
      margin: null,
    }));

    return [...rows, ...placeholders];
  }

  private resolveMixedChartSalesUnit(
    row: any,
    chartConfig: any,
    years: any[],
    salesPrefix: string,
  ): { divisor?: number; barLabelSuffix?: string; leftTitle?: string } {
    if (chartConfig?.dynamicSalesUnitByValue === true) {
      return {
        divisor: undefined,
        barLabelSuffix: '',
        leftTitle: this.resolveMixedChartSalesTitle(chartConfig?.leftTitle, '', true),
      };
    }

    if (chartConfig?.consistentSalesUnit !== true) {
      return {
        divisor: Number.isFinite(Number(chartConfig?.salesDivisor)) ? Number(chartConfig.salesDivisor) : undefined,
        barLabelSuffix: String(chartConfig?.barLabelSuffix || '').trim() || undefined,
      };
    }

    const salesValues = years
      .map((year: any) => Number(row?.[`${salesPrefix}${year}`]))
      .filter((value) => Number.isFinite(value));
    const maxAbsSales = salesValues.length
      ? Math.max(...salesValues.map((value) => Math.abs(value)))
      : 0;

    let divisor: number | undefined;
    let unitSuffix = '';
    if (maxAbsSales >= 1_000_000) {
      divisor = 1_000_000;
      unitSuffix = 'M';
    } else if (maxAbsSales >= 1_000) {
      divisor = 1_000;
      unitSuffix = 'K';
    }

    return {
      divisor,
      barLabelSuffix: unitSuffix || undefined,
      leftTitle: this.resolveMixedChartSalesTitle(chartConfig?.leftTitle, unitSuffix),
    };
  }

  private resolveMixedChartSalesTitle(configuredTitle: any, unitSuffix: string, dynamicByValue = false): string | undefined {
    const title = String(configuredTitle || '').trim();
    if (!title) {
      if (dynamicByValue) {
        return 'Sales ($)';
      }
      return unitSuffix ? `Sales ($${unitSuffix})` : undefined;
    }

    if (dynamicByValue) {
      if (/\(\$\s*[KMB]\s*\)/i.test(title)) {
        return title.replace(/\(\$\s*[KMB]\s*\)/i, '($)');
      }

      return title;
    }

    if (!unitSuffix) {
      return title;
    }

    if (/\(\$\s*[KMB]\s*\)/i.test(title)) {
      return title.replace(/\(\$\s*[KMB]\s*\)/i, `($${unitSuffix})`);
    }

    if (/\(\$\s*\)/i.test(title)) {
      return title.replace(/\(\$\s*\)/i, `($${unitSuffix})`);
    }

    return title;
  }

  private buildCagrPatchFromResponse(response: any, chartConfig: any): any {
    const row = this.resolveHoverResponseRow(response, chartConfig);
    if (!row) {
      return { data: [] };
    }

    const years = Array.isArray(chartConfig.years)
      ? chartConfig.years
      : [2020, 2021, 2022, 2023, 2024, 2025];
    const salesPrefix = chartConfig.salesPrefix || 'sales_';
    const cagrValueKey = chartConfig.cagrValueKey || 'cagr_volume';
    // The sales row turns a missing CAGR into 0. cagrValuePath can point at a row that keeps it empty
    // (e.g. the raw customer/engine row); an empty value there means no CAGR, not 0%. If that row
    // isn't in the response at all, the sales row's value is used as before.
    const configuredCagr = chartConfig?.cagrValuePath
      ? this.resolveConfiguredProgressPath(response, chartConfig.cagrValuePath)
      : undefined;
    const rawCagr = configuredCagr !== undefined ? configuredCagr : row[cagrValueKey];
    const cagrValue = this.hasAvailableNumericValue(rawCagr)
      ? this.toDisplayNumber(rawCagr, undefined, chartConfig.cagrMultiplier)
      : Number.NaN;
    const hideZeroSalesYears = chartConfig?.hideZeroSalesYears !== false;
    const data = years
      .map((year: any) => ({
        year: String(year),
        sales: this.toDisplayNumber(row[`${salesPrefix}${year}`], chartConfig.salesDivisor),
      }))
      .filter((entry: { year: string; sales: number }) => !hideZeroSalesYears || entry.sales > 0);

    return {
      cagrLabel: this.formatCagrLabel(cagrValue),
      progressValue: cagrValue,
      progressValueLabel: this.formatCagrLabel(cagrValue),
      data,
      // Every other hover-type branch in buildHoverResponsePatch includes this so the generic
      // rowProgress marker/label/scale resolvers (which read activeHoverConfig.responseData via
      // resolveConfiguredProgressValue) can look up a configured markerValuePath. Omitting it
      // here silently broke the CAGR row's progress bar: the marker value resolved to null,
      // so the bar fell back to status-based positioning with no value label at all.
      responseData: response,
    };
  }

  private resolveHoverResponseRow(response: any, chartConfig: any): any {
    if (chartConfig?.matchCustomerSalesToEngineLevel === true || chartConfig?.matchCustomerSalesByApplication === true) {
      const matchedRow = this.resolveMatchedCustomerSalesRow(response);
      if (matchedRow) {
        return matchedRow;
      }
    }

    return (
      this.resolveHoverPath(response, chartConfig.responseRowPath || chartConfig.dataPath || 'responseList.0.customer_sales.0') ||
      this.resolveHoverPath(response, 'responseList.0.data.0.customer_sales.0') ||
      this.findFirstObjectForKey(response, chartConfig.customerSalesKey || 'customer_sales')
    );
  }

  private buildMarketSharePatchFromResponse(response: any, chartConfig: any): any {
    const row =
      this.resolveHoverPath(response, chartConfig.responseRowPath || chartConfig.dataPath || 'responseList.0.competitive_factors.0') ||
      this.findFirstObjectForKey(response, chartConfig.responseKey || 'competitive_factors');

    if (!row) {
      return {};
    }

    const configuredKeys = Array.isArray(chartConfig.valueKey)
      ? chartConfig.valueKey
      : chartConfig.valueKey
        ? [chartConfig.valueKey]
        : [];
    // strictValueKey: read only the configured column. Without it a missing column falls back to the
    // market-share columns below, which would show the wrong metric for a non-market-share popup.
    const rawValue = this.readFirstPresent(row, chartConfig.strictValueKey === true
      ? configuredKeys
      : [
        ...configuredKeys,
        'cummins_share_in_companies_market_share_in_the_segment',
        'mins_share_in_companies_market_share_in_the_segment',
        'companies_market_share_in_the_segment',
      ]);
    const value = this.toPercentValue(rawValue, chartConfig.valueMultiplier);

    return {
      responseData: response,
      value,
      valueLabel: Number.isFinite(value) ? `${Number(value.toFixed(2))}%` : '-',
      ...(chartConfig.descriptionKey && row[chartConfig.descriptionKey] !== undefined
        ? { description: String(row[chartConfig.descriptionKey]) }
        : {}),
    };
  }

  private readFirstAvailable(source: any, keys: string | string[]): any {
    const keyList = Array.isArray(keys) ? keys : [keys];
    for (const key of keyList) {
      const value = source?.[key];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return undefined;
  }

  private readFirstPresent(source: any, keys: string | string[]): any {
    const keyList = Array.isArray(keys) ? keys : [keys];
    for (const key of keyList) {
      if (source && Object.prototype.hasOwnProperty.call(source, key)) {
        return source[key];
      }
    }
    return undefined;
  }

  private resolveHoverPath(source: any, path: string): any {
    return String(path || '')
      .split('.')
      .filter(Boolean)
      .reduce((current, segment) => current?.[segment], source);
  }

  private findFirstObjectForKey(source: any, key: string): any {
    const value = this.findValueForKey(source, key);
    return Array.isArray(value) ? value[0] : value;
  }

  private findValueForKey(source: any, key: string): any {
    if (!source || !key) {
      return undefined;
    }

    if (Array.isArray(source)) {
      for (const item of source) {
        const value = this.findValueForKey(item, key);
        if (value !== undefined) {
          return value;
        }
      }
      return undefined;
    }

    if (typeof source !== 'object') {
      return undefined;
    }

    if (source[key] !== undefined) {
      return source[key];
    }

    for (const value of Object.values(source)) {
      const found = this.findValueForKey(value, key);
      if (found !== undefined) {
        return found;
      }
    }

    return undefined;
  }

  private toDisplayNumber(value: any, divisor?: any, multiplier?: any): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return 0;
    }

    const numericDivisor = Number(divisor);
    if (Number.isFinite(numericDivisor) && numericDivisor !== 0) {
      return Number((parsed / numericDivisor).toFixed(2));
    }

    const numericMultiplier = Number(multiplier);
    if (Number.isFinite(numericMultiplier)) {
      return Number((parsed * numericMultiplier).toFixed(2));
    }

    return Number(parsed.toFixed(2));
  }

  private toPercentValue(value: any, multiplier?: any): number {
    if (value === null || value === undefined || value === '') {
      return Number.NaN;
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return Number.NaN;
    }

    const configuredMultiplier = Number(multiplier);
    if (Number.isFinite(configuredMultiplier)) {
      return Number((parsed * configuredMultiplier).toFixed(2));
    }

    const normalized = Math.abs(parsed) <= 1 ? parsed * 100 : parsed;
    return Number(normalized.toFixed(2));
  }

  private formatCagrLabel(value: number): string {
    if (!Number.isFinite(value)) {
      return '-';
    }

    return `${Number(value.toFixed(2))}%`;
  }

  private buildSalesProgressBoundaryLabels(response: any, row: any): Array<{ text: string; left: number; edgeClass: string }> {
    if (!this.isSalesProgressRow(row)) {
      return [];
    }

    const rowProgressConfig = this.getResolvedRowProgressConfig(row);
    const configuredLabels = Array.isArray(rowProgressConfig?.scaleLabels) ? rowProgressConfig.scaleLabels : [];
    const configs = configuredLabels.length
      ? configuredLabels.map((label: any) => ({
          path: label?.valuePath,
          key: label?.valueKey,
          left: Number(label?.position),
          divisor: label?.divisor ?? rowProgressConfig?.valueDivisor,
          multiplier: label?.multiplier ?? rowProgressConfig?.valueMultiplier,
          decimals: label?.decimals ?? rowProgressConfig?.valueDecimals,
          suffix: label?.suffix ?? rowProgressConfig?.valueSuffix,
        }))
      : [
          { path: 'responseList.0.customer_level_screen.0.sales_grade_0', left: 0, divisor: 1000000, decimals: 1, suffix: 'M' },
          { path: 'responseList.0.customer_level_screen.0.sales_grade_1', left: 33.3333, divisor: 1000000, decimals: 1, suffix: 'M' },
          { path: 'responseList.0.customer_level_screen.0.sales_grade_2', left: 66.6667, divisor: 1000000, decimals: 1, suffix: 'M' },
          { path: 'responseList.0.customer_level_screen.0.sales_grade_3', left: 100, divisor: 1000000, decimals: 1, suffix: 'M' },
        ];

    return configs
      .map((config: { path?: any; key?: any; left: number; divisor?: any; multiplier?: any; decimals?: any; suffix?: any }) => {
        const path = String(config.path || '').trim();
        const key = String(config.key || '').trim();
        const value = path
          ? this.resolveConfiguredProgressPath(response, path)
          : key
            ? this.resolveConfiguredProgressPath(response, key)
            : undefined;
        const text = this.formatProgressValueLabel(Number.isFinite(Number(value)) ? Number(value) : null, {
          divisor: config.divisor,
          multiplier: config.multiplier,
          decimals: config.decimals,
          suffix: config.suffix,
        });

        if (!text || !Number.isFinite(config.left)) {
          return null;
        }

        const safeLeft = Math.max(0, Math.min(100, config.left));
        return {
          text,
          left: safeLeft,
          edgeClass: safeLeft <= 0 ? 'start' : safeLeft >= 100 ? 'end' : 'middle',
        };
      })
      .filter((label: { text: string; left: number; edgeClass: string } | null): label is { text: string; left: number; edgeClass: string } => Boolean(label));
  }

  private getFallbackSalesProgressScaleLabels(): Array<{ text: string; left: number; edgeClass: string }> {
    const configs = [
      { path: 'responseList.0.customer_level_screen.0.sales_grade_0', left: 0 },
      { path: 'responseList.0.customer_level_screen.0.sales_grade_1', left: 33.3333 },
      { path: 'responseList.0.customer_level_screen.0.sales_grade_2', left: 66.6667 },
      { path: 'responseList.0.customer_level_screen.0.sales_grade_3', left: 100 },
    ];

    return configs
      .map((config) => {
        const value = this.resolveConfiguredProgressValue(config.path, '');
        const text = this.formatProgressValueLabel(value, {
          divisor: 1000000,
          decimals: 1,
          suffix: 'M',
        });

        if (!text) {
          return null;
        }

        const edgeClass = config.left <= 0 ? 'start' : config.left >= 100 ? 'end' : 'middle';
        return { text, left: config.left, edgeClass };
      })
      .filter((label): label is { text: string; left: number; edgeClass: string } => Boolean(label));
  }

  private resolveConfiguredScaleStops(
    config: any,
    responseSource?: any,
  ): Array<{ rawValue: number; value: number; position: number; divisor?: any; multiplier?: any; decimals?: any; suffix?: any }> {
    type ProgressScaleStop = {
      rawValue: number;
      value: number;
      position: number;
      divisor?: any;
      multiplier?: any;
      decimals?: any;
      suffix?: any;
    };

    const configuredLabels = Array.isArray(config?.scaleLabels) ? config.scaleLabels : [];
    const resolvedStops = configuredLabels
      .map((label: any) => {
        const rawValue = responseSource
          ? this.resolveConfiguredProgressPath(responseSource, label?.valuePath || label?.valueKey)
          : this.resolveConfiguredProgressValue(label?.valuePath, label?.valueKey);
        const numericValue = Number(this.normalizeProgressMetricValue(
          rawValue,
          label?.divisor ?? config?.valueDivisor,
          label?.multiplier ?? config?.valueMultiplier,
        ));
        const configuredPosition = Number(label?.position);
        if (!Number.isFinite(numericValue)) {
          return null;
        }

        return {
          rawValue: Number(rawValue),
          value: numericValue,
          position: configuredPosition,
          divisor: label?.divisor ?? config?.valueDivisor,
          multiplier: label?.multiplier ?? config?.valueMultiplier,
          decimals: label?.decimals ?? config?.valueDecimals,
          suffix: label?.suffix ?? config?.valueSuffix,
        };
      })
      .filter((label: ProgressScaleStop | null): label is ProgressScaleStop => Boolean(label))
      .sort((left: ProgressScaleStop, right: ProgressScaleStop) => left.value - right.value);

    if (!resolvedStops.length) {
      return [];
    }

    if (!this.shouldUseContinuousScalePositions(config)) {
      return resolvedStops.map((label: ProgressScaleStop) => ({
        ...label,
        position: Math.max(0, Math.min(100, Number.isFinite(label.position) ? label.position : 0)),
      }));
    }

    const minValue = resolvedStops[0].value;
    const maxValue = resolvedStops[resolvedStops.length - 1].value;
    const span = maxValue - minValue;
    if (!(span > 0)) {
      return resolvedStops.map((label: ProgressScaleStop, index: number) => ({
        ...label,
        position: resolvedStops.length === 1 ? 0 : (index / (resolvedStops.length - 1)) * 100,
      }));
    }

    return resolvedStops.map((label: ProgressScaleStop) => ({
      ...label,
      position: ((label.value - minValue) / span) * 100,
    }));
  }

  private shouldUseContinuousScalePositions(config: any): boolean {
    const mode = String(config?.scalePositionMode || config?.positionMode || '').trim().toLowerCase();
    return mode === 'continuous' || mode === 'proportional' || mode === 'value';
  }
}
